import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CardActionArea from '@mui/material/CardActionArea';
import Button from '@mui/material/Button';
import storyBaseService from '../service/story-base.service';
import storyRunService from '../service/story-run.service';
import storyImagePlanService from '../service/story-image-plan.service';
import { StoryBaseEntity, StoryRunEntity, StoryImagePlanEntity } from '../types/entities';
import voiceService from '../service/voice.service';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import FaceIcon from '@mui/icons-material/Face';
import Face2Icon from '@mui/icons-material/Face2';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

const TemplateDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [item, setItem] = React.useState<StoryBaseEntity | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [runs, setRuns] = React.useState<StoryRunEntity[] | null>(null);
  const [runsLoading, setRunsLoading] = React.useState<boolean>(false);
  const [runsError, setRunsError] = React.useState<string | null>(null);
  const [voices, setVoices] = React.useState<any[]>([]);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const [playingKey, setPlayingKey] = React.useState<string | null>(null);
  const [storyExpanded, setStoryExpanded] = React.useState<boolean>(false);
  const [storyPage, setStoryPage] = React.useState<number>(1);
  const [selectedRunId, setSelectedRunId] = React.useState<string | null>(null);
  const [plans, setPlans] = React.useState<StoryImagePlanEntity[] | null>(null);
  const [plansLoading, setPlansLoading] = React.useState<boolean>(false);
  const [plansError, setPlansError] = React.useState<string | null>(null);
  const step3Ref = React.useRef<HTMLDivElement | null>(null);
  const [planModalOpen, setPlanModalOpen] = React.useState<boolean>(false);
  const [planIndex, setPlanIndex] = React.useState<number>(0);
  const touchStartX = React.useRef<number | null>(null);
  const SWIPE_THRESHOLD = 40;
  const [fullImageOpen, setFullImageOpen] = React.useState<boolean>(false);
  const [descExpanded, setDescExpanded] = React.useState<boolean>(false);
  const [textExpanded, setTextExpanded] = React.useState<boolean>(false);
  const modalAudioRef = React.useRef<HTMLAudioElement | null>(null);
  const [modalAudioPlaying, setModalAudioPlaying] = React.useState<boolean>(false);
  const [modalAudioDuration, setModalAudioDuration] = React.useState<number | null>(null);
  // Start Run dialog state
  const [startOpen, setStartOpen] = React.useState<boolean>(false);
  const [startLoading, setStartLoading] = React.useState<boolean>(false);
  const [startError, setStartError] = React.useState<string | null>(null);
  const [refUrl, setRefUrl] = React.useState<string>('');
  const [imageFile, setImageFile] = React.useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = React.useState<string | null>(null);
  const [optIsDialog, setOptIsDialog] = React.useState<boolean | undefined>(undefined);
  const [maxImageCount, setMaxImageCount] = React.useState<string>('');
  const [totalNarrationMs, setTotalNarrationMs] = React.useState<string>('');
  const [imageIntervalSeconds, setImageIntervalSeconds] = React.useState<string>('');
  const [userNote, setUserNote] = React.useState<string>('');
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = React.useState<boolean>(false);
  const [editSubmitting, setEditSubmitting] = React.useState<boolean>(false);
  const [editError, setEditError] = React.useState<string | null>(null);
  const [editTitle, setEditTitle] = React.useState<string>('');
  const [editDescription, setEditDescription] = React.useState<string>('');
  const [editFullStory, setEditFullStory] = React.useState<string>('');
  const [editLanguage, setEditLanguage] = React.useState<string>('tr');
  const [editIsDialog, setEditIsDialog] = React.useState<boolean>(false);
  const [editTarget, setEditTarget] = React.useState<'short' | 'youtube' | 'other'>('youtube');
  const [editEstimatedDurationMs, setEditEstimatedDurationMs] = React.useState<string>('');
  const [editCharacters, setEditCharacters] = React.useState<Array<{ name: string; voiceId: string }>>([]);
  const [messageOpen, setMessageOpen] = React.useState<boolean>(false);
  const [messageText, setMessageText] = React.useState<string>('');
  const [deleteOpen, setDeleteOpen] = React.useState<boolean>(false);

  React.useEffect(() => {
    // Modal açılınca veya plan değişince kısaltmaları sıfırla
    setDescExpanded(false);
    setTextExpanded(false);
    setModalAudioPlaying(false);
    setModalAudioDuration(null);
    if (modalAudioRef.current) {
      try { modalAudioRef.current.pause(); } catch {}
      try { modalAudioRef.current.currentTime = 0; } catch {}
    }
  }, [planIndex, planModalOpen]);

  React.useEffect(() => {
    // Dosya önizleme URL yönetimi
    if (imageFile) {
      const url = URL.createObjectURL(imageFile);
      setImagePreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setImagePreviewUrl(null);
    }
  }, [imageFile]);

  React.useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const data = await storyBaseService.getStoryBase(id);
        if (!alive) return;
        setItem(data);
      } catch (e: any) {
        if (!alive) return;
        setError(e?.response?.data?.message || e?.message || 'Detay yüklenemedi');
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [id]);

  React.useEffect(() => {
    if (!item) return;
    setEditTitle(item.title || '');
    setEditDescription((item as any).description || '');
    setEditFullStory(item.fullStory || '');
    setEditLanguage(item.language || 'tr');
    setEditIsDialog(!!item.isDialog);
    setEditTarget(item.target || 'youtube');
    setEditEstimatedDurationMs(
      typeof (item as any).estimatedDurationMs === 'number' ? String((item as any).estimatedDurationMs) : ''
    );
    const chars = Array.isArray(item.charactersJson)
      ? (item.charactersJson as any[]).map((c) => ({ name: c.name || '', voiceId: c.voiceId || '' }))
      : [];
    setEditCharacters(chars.length ? chars : [{ name: '', voiceId: '' }]);
  }, [item]);

  const speakerForIndex = (idx: number) => {
    const letters = ['A', 'B', 'C', 'D'];
    return `speaker ${letters[idx] || idx + 1}`;
  };

  React.useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!id) return;
      setRunsLoading(true);
      setRunsError(null);
      try {
        const data = await storyRunService.getRunsByBase(id);
        if (!alive) return;
        setRuns(Array.isArray(data) ? data : []);
      } catch (e: any) {
        if (!alive) return;
        setRunsError(e?.response?.data?.message || e?.message || 'Runs yüklenemedi');
      } finally {
        if (alive) setRunsLoading(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [id]);

  React.useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        const list = await voiceService.getActiveVoices();
        if (!alive) return;
        const uniqById: Record<string, any> = {};
        (list || []).forEach((v: any) => { if (v && v.id) uniqById[v.id] = v; });
        setVoices(Object.values(uniqById));
      } catch {}
    };
    run();
    return () => { alive = false; };
  }, []);

  React.useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    };
  }, []);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Template Detail</Typography>
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography>Yükleniyor...</Typography>
        </Box>
      )}
      {/* Start Run Dialog */}
      <Dialog open={startOpen} onClose={() => !startLoading && setStartOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Start New Run</Typography>
          <IconButton onClick={() => !startLoading && setStartOpen(false)} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {startError && <Alert severity="error" sx={{ mb: 2 }}>{startError}</Alert>}
          <Stack spacing={2}>
            <TextField
              label="Reference Image URL"
              value={refUrl}
              onChange={(e) => { setRefUrl(e.target.value); if (e.target.value) setImageFile(null); }}
              placeholder="https://..."
              fullWidth
            />
            <Box>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderStyle: 'dashed',
                  bgcolor: 'background.default',
                }}
              >
                {!imageFile && (
                  <Button
                    fullWidth
                    component="label"
                    sx={{
                      py: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    <CloudUploadIcon fontSize="large" />
                    <Typography variant="body2">Upload Image</Typography>
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => {
                        const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                        setImageFile(f);
                        if (f) setRefUrl('');
                      }}
                    />
                  </Button>
                )}
                {imageFile && imagePreviewUrl && (
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <Box
                        component="img"
                        src={imagePreviewUrl}
                        alt="Preview"
                        sx={{ maxHeight: 180, maxWidth: '100%', objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                      />
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => setImageFile(null)}
                      sx={{ position: 'absolute', right: 8, bottom: 8, bgcolor: 'error.main', color: 'error.contrastText', '&:hover': { bgcolor: 'error.dark' } }}
                      aria-label="Clear image"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Paper>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }} color="text.secondary">
                If you upload an image, the URL will be cleared automatically.
              </Typography>
            </Box>
            <FormControlLabel
              control={<Switch checked={!!optIsDialog} onChange={(e) => setOptIsDialog(e.target.checked)} />}
              label="Is Dialog? (optional)"
            />
            <TextField
              label="Max Image Count (1-100)"
              type="number"
              inputProps={{ min: 1, max: 100 }}
              value={maxImageCount}
              onChange={(e) => setMaxImageCount(e.target.value)}
              fullWidth
            />
            <TextField
              label="Total Narration (ms)"
              type="number"
              inputProps={{ min: 1000 }}
              value={totalNarrationMs}
              onChange={(e) => setTotalNarrationMs(e.target.value)}
              fullWidth
            />
            <TextField
              label="Image Interval (seconds)"
              type="number"
              inputProps={{ min: 1 }}
              value={imageIntervalSeconds}
              onChange={(e) => setImageIntervalSeconds(e.target.value)}
              fullWidth
            />
            <TextField
              label="User Note"
              multiline
              minRows={2}
              value={userNote}
              onChange={(e) => setUserNote(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartOpen(false)} disabled={startLoading}>Cancel</Button>
          <Button
            variant="contained"
            disabled={startLoading || !id}
            onClick={async () => {
              if (!id) return;
              setStartError(null);
              setStartLoading(true);
              try {
                await storyRunService.startRun({
                  baseStoryId: id,
                  referenceImageUrl: refUrl || undefined,
                  isDialog: typeof optIsDialog === 'boolean' ? optIsDialog : undefined,
                  maxImageCount: maxImageCount ? Number(maxImageCount) : undefined,
                  totalNarrationMs: totalNarrationMs ? Number(totalNarrationMs) : undefined,
                  imageIntervalSeconds: imageIntervalSeconds ? Number(imageIntervalSeconds) : undefined,
                  userNote: userNote || undefined,
                  imageFile: imageFile || null,
                });
                // Refresh runs
                const data = await storyRunService.getRunsByBase(id);
                setRuns(Array.isArray(data) ? data : []);
                setStartOpen(false);
                // reset fields
                setRefUrl(''); setImageFile(null); setOptIsDialog(undefined);
                setMaxImageCount(''); setTotalNarrationMs(''); setImageIntervalSeconds(''); setUserNote('');
              } catch (e: any) {
                setStartError(e?.response?.data?.message || e?.message || 'Başlatma başarısız');
              } finally {
                setStartLoading(false);
              }
            }}
          >
            {startLoading ? 'Starting...' : 'Start'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Message Dialog */}
      <Dialog open={messageOpen} onClose={() => setMessageOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Info</DialogTitle>
        <DialogContent dividers>
          <Typography>{messageText}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setMessageOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Base Story</DialogTitle>
        <DialogContent dividers>
          <Typography>Bu base story kalıcı olarak silinecek. Devam etmek istiyor musunuz?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Vazgeç</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (!id) return;
              try {
                const res = await storyBaseService.deleteStoryBase(id);
                const msg = res?.message || 'Base Story Deleted';
                setMessageText(msg);
                setMessageOpen(true);
                setDeleteOpen(false);
                // küçük gecikme ile ana sayfaya dön
                setTimeout(() => navigate('/video-creator'), 300);
              } catch (e: any) {
                setMessageText(e?.response?.data?.message || e?.message || 'Silme başarısız');
                setMessageOpen(true);
                setDeleteOpen(false);
              }
            }}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>
      {/* Update Base Story Dialog */}
      <Dialog open={editOpen} onClose={() => !editSubmitting && setEditOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Update Base Story</Typography>
          <IconButton onClick={() => !editSubmitting && setEditOpen(false)} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <Stack spacing={2}>
            <TextField
              label="Title"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              fullWidth
            />
            <TextField
              label="Full Story"
              value={editFullStory}
              onChange={(e) => setEditFullStory(e.target.value)}
              required
              fullWidth
              multiline
              minRows={15}
              maxRows={15}
              sx={{ '& textarea': { overflow: 'auto' } }}
            />
            <TextField
              label="Estimated Duration (ms)"
              type="number"
              value={editEstimatedDurationMs}
              onChange={(e) => setEditEstimatedDurationMs(e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="edit-lang-label">Language</InputLabel>
              <Select
                labelId="edit-lang-label"
                label="Language"
                value={editLanguage}
                onChange={(e) => setEditLanguage(e.target.value)}
              >
                <MenuItem value="tr">TR</MenuItem>
                <MenuItem value="en">EN</MenuItem>
                <MenuItem value="de">DE</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="edit-target-label">Target</InputLabel>
              <Select
                labelId="edit-target-label"
                label="Target"
                value={editTarget}
                onChange={(e) => setEditTarget(e.target.value as any)}
              >
                <MenuItem value="short">short</MenuItem>
                <MenuItem value="youtube">youtube</MenuItem>
                <MenuItem value="other">other</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={editIsDialog} onChange={(e) => setEditIsDialog(e.target.checked)} />}
              label="Is Dialog?"
            />
            <Divider />
            <Typography variant="subtitle1">Characters</Typography>
            <Typography variant="body2" color="text.secondary">
              {editIsDialog ? 'Dialog açık: En fazla 5 karakter ekleyebilirsiniz.' : 'Dialog kapalı: Sadece 1 karakter ekleyebilirsiniz.'}
            </Typography>
            <Stack spacing={1}>
              {editCharacters.map((ch, idx) => (
                <Stack key={idx} spacing={1}>
                  <Typography variant="caption" color="text.secondary">{speakerForIndex(idx)}</Typography>
                  <TextField
                    label="Character Name"
                    value={ch.name}
                    onChange={(e) => {
                      const next = [...editCharacters];
                      next[idx] = { ...next[idx], name: e.target.value };
                      setEditCharacters(next);
                    }}
                    required
                  />
                  <FormControl fullWidth>
                    <InputLabel id={`edit-voice-label-${idx}`}>Voice</InputLabel>
                    <Select
                      labelId={`edit-voice-label-${idx}`}
                      label="Voice"
                      value={ch.voiceId}
                      onChange={(e) => {
                        const next = [...editCharacters];
                        next[idx] = { ...next[idx], voiceId: e.target.value as string };
                        setEditCharacters(next);
                      }}
                    >
                      {voices.map((v: any) => {
                        const samples = String(v.sampleAudioPath || '')
                          .split('&&&')
                          .map((s) => s.trim())
                          .filter(Boolean);
                        return (
                          <MenuItem key={v.id} value={v.externalVoiceId || ''} disabled={!v.externalVoiceId}>
                            <Stack spacing={0.5} sx={{ width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <Avatar sx={{ width: 22, height: 22, fontSize: 14, bgcolor: v.gender === 'woman' ? 'secondary.main' : 'primary.main' }}>
                                  {v.gender === 'woman' ? (
                                    <Face2Icon fontSize="inherit" />
                                  ) : (
                                    <FaceIcon fontSize="inherit" />
                                  )}
                                </Avatar>
                                <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {v.name}
                                  {v.description ? ` — ${v.description}` : ''}
                                  {!v.externalVoiceId ? ' (no externalVoiceId)' : ''}
                                </Typography>
                              </Box>
                              {samples.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {samples.map((url: string, i: number) => {
                                    const key = `${v.id}-${i}`;
                                    const isPlaying = playingKey === key;
                                    return (
                                      <IconButton
                                        key={i}
                                        size="small"
                                        color={isPlaying ? 'primary' : 'default'}
                                        onMouseDown={(e) => { e.stopPropagation(); }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          try {
                                            if (isPlaying) {
                                              if (audioRef.current) {
                                                audioRef.current.pause();
                                                audioRef.current.currentTime = 0;
                                                audioRef.current = null;
                                              }
                                              setPlayingKey(null);
                                              return;
                                            }
                                            if (audioRef.current) {
                                              audioRef.current.pause();
                                              audioRef.current.currentTime = 0;
                                              audioRef.current = null;
                                            }
                                            const audio = new Audio(url);
                                            audioRef.current = audio;
                                            setPlayingKey(key);
                                            audio.onended = () => {
                                              setPlayingKey(null);
                                              if (audioRef.current === audio) audioRef.current = null;
                                            };
                                            audio.onerror = () => {
                                              setPlayingKey(null);
                                              if (audioRef.current === audio) audioRef.current = null;
                                            };
                                            audio.play().catch(() => setPlayingKey(null));
                                          } catch { setPlayingKey(null); }
                                        }}
                                        aria-label={isPlaying ? `Stop sample ${i + 1}` : `Play sample ${i + 1}`}
                                      >
                                        {isPlaying ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                                      </IconButton>
                                    );
                                  })}
                                </Box>
                              )}
                            </Stack>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <Box>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => {
                        const next = editCharacters.filter((_, i) => i !== idx);
                        setEditCharacters(next.length ? next : [{ name: '', voiceId: '' }]);
                      }}
                      disabled={editCharacters.length <= 1}
                    >
                      Remove Character
                    </Button>
                  </Box>
                  <Divider />
                </Stack>
              ))}
              <Box>
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (editIsDialog) {
                      if (editCharacters.length < 5) setEditCharacters([...editCharacters, { name: '', voiceId: '' }]);
                    } else {
                      if (editCharacters.length < 1) setEditCharacters([{ name: '', voiceId: '' }]);
                    }
                  }}
                  disabled={!editIsDialog ? editCharacters.length >= 1 : editCharacters.length >= 5}
                >
                  Add Character
                </Button>
              </Box>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editSubmitting}>Cancel</Button>
          <Button
            variant="contained"
            disabled={editSubmitting || !id}
            onClick={async () => {
              if (!id) return;
              setEditError(null);
              // Basic client validations
              if (!editTitle || !editFullStory || !editLanguage || !editTarget) {
                setEditError('Lütfen tüm zorunlu alanları doldurun.');
                return;
              }
              if (!editCharacters.length) {
                setEditError('En az bir karakter olmalı.');
                return;
              }
              if (!editIsDialog && editCharacters.length !== 1) {
                setEditError('Dialog kapalıyken yalnızca 1 karakter olabilir.');
                return;
              }
              if (editIsDialog && editCharacters.length > 5) {
                setEditError('Dialog açıkken en fazla 5 karakter olabilir.');
                return;
              }
              const invalid = editCharacters.some((c) => !c.name || !c.voiceId);
              if (invalid) {
                setEditError('Tüm karakterler için isim ve voice zorunludur.');
                return;
              }
              const charactersJson = editCharacters.map((c, idx) => ({
                name: c.name,
                speaker: speakerForIndex(idx),
                voiceId: c.voiceId,
              }));
              try {
                setEditSubmitting(true);
                const updated = await storyBaseService.updateStoryBase(id, {
                  title: editTitle,
                  description: editDescription || null,
                  fullStory: editFullStory,
                  language: editLanguage,
                  isDialog: editIsDialog,
                  target: editTarget,
                  estimatedDurationMs: editEstimatedDurationMs ? Number(editEstimatedDurationMs) : null,
                  charactersJson,
                });
                const msg = updated?.message || 'Base Story Updated';
                if (updated?.isNewStory && updated?.result?.id) {
                  setMessageText(`${msg} \n\nDegisikliklerden dolayi Yeni bir story olusturuldu.`);
                  setMessageOpen(true);
                  setEditOpen(false);
                  navigate(`/templates/${updated.result.id}`);
                } else {
                  // Refetch current item to ensure we show the persisted server state
                  try {
                    const fresh = await storyBaseService.getStoryBase(id);
                    setItem(fresh);
                  } catch {}
                  setMessageText(msg);
                  setMessageOpen(true);
                  setEditOpen(false);
                }
              } catch (e: any) {
                setEditError(e?.response?.data?.message || e?.message || 'Güncelleme başarısız');
              } finally {
                setEditSubmitting(false);
              }
            }}
          >
            {editSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      {!loading && error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && item && (
        <Box sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
        }}>
          <Box>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6">{item.title}</Typography>
                <Box>
                  <IconButton size="small" onClick={() => { setEditError(null); setEditOpen(true); }} aria-label="Edit base story" sx={{ mr: 0.5 }}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setDeleteOpen(true)} aria-label="Delete base story">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
              <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                <Chip label={(item.language || '').toUpperCase()} size="small" />
                <Chip label={item.target} size="small" />
                <Chip label={item.isDialog ? 'Dialog' : 'Single'} size="small" />
                <Chip label={`Version : ${item.version}`} size="small" />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {new Date(item.updatedAt || item.createdAt).toLocaleString()}
              </Typography>
              {typeof item.estimatedDurationMs === 'number' && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Estimated Duration: {item.estimatedDurationMs} ms - {Math.round((item.estimatedDurationMs || 0) / 1000)} sn
                </Typography>
              )}
              <Divider sx={{ my: 2 }} />
              {(() => {
                const desc = String((item as any).description || '').trim();
                if (!desc) return null;
                const preview = desc.slice(0, 250);
                const hasMore = desc.length > 250;
                return (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Description</Typography>
                    <Box sx={{ whiteSpace: 'pre-wrap' }}>
                      <Typography variant="body1">{preview}{hasMore ? '…' : ''}</Typography>
                    </Box>
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                );
              })()}
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Full Story</Typography>
              {(() => {
                const full = item.fullStory || '';
                if (!storyExpanded) {
                  const preview = full.slice(0, 250);
                  const hasMore = full.length > 250;
                  return (
                    <Stack spacing={1}>
                      <Box sx={{ whiteSpace: 'pre-wrap' }}>
                        <Typography variant="body1">{preview}{hasMore ? '…' : ''}</Typography>
                      </Box>
                      {hasMore && (
                        <Box>
                          <Button size="small" variant="outlined" onClick={() => { setStoryExpanded(true); setStoryPage(1); }}>
                            Read more
                          </Button>
                        </Box>
                      )}
                    </Stack>
                  );
                }
                const pageSize = 4500;
                const totalPages = Math.max(1, Math.ceil(full.length / pageSize));
                const clampedPage = Math.min(Math.max(storyPage, 1), totalPages);
                const start = (clampedPage - 1) * pageSize;
                const end = start + pageSize;
                const chunk = full.slice(start, end);
                const goPage = (p: number) => setStoryPage(Math.min(Math.max(p, 1), totalPages));
                return (
                  <Stack spacing={1}>
                    <Box sx={{ whiteSpace: 'pre-wrap', minHeight: 320 }}>
                      <Typography variant="body1">{chunk}</Typography>
                    </Box>
                    {totalPages > 1 && (
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <IconButton size="small" onClick={() => goPage(clampedPage - 1)} disabled={clampedPage <= 1} aria-label="Previous page">
                          <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                        <Stack direction="row" spacing={0.5}>
                          {Array.from({ length: totalPages }).map((_, i) => {
                            const p = i + 1;
                            const active = p === clampedPage;
                            return (
                              <Button key={p} size="small" variant={active ? 'contained' : 'text'} onClick={() => goPage(p)}>
                                {p}
                              </Button>
                            );
                          })}
                        </Stack>
                        <IconButton size="small" onClick={() => goPage(clampedPage + 1)} disabled={clampedPage >= totalPages} aria-label="Next page">
                          <ChevronRightIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    )}
                  </Stack>
                );
              })()}
            </Paper>
          </Box>
          <Box>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Characters</Typography>
              <Stack spacing={1}>
                {Array.isArray(item.charactersJson) && item.charactersJson.length > 0 ? (
                  item.charactersJson.map((c: any, idx: number) => {
                    const v = voices[c.voiceId];
                    const samples = ((v?.sampleAudioPath as string) || '')
                      .split('&&&')
                      .map((s) => s.trim())
                      .filter(Boolean);
                    return (
                      <Paper key={idx} variant="outlined" sx={{ p: 1 }}>
                        <Stack spacing={0.5}>
                          <Typography variant="body2"><strong>{c.speaker}</strong> — {c.name}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary">
                              {v ? v.name : c.voiceId}
                            </Typography>
                            {samples.map((url, i) => {
                              const key = `${c.voiceId}-${i}`;
                              const isPlaying = playingKey === key;
                              return (
                                <IconButton
                                  key={i}
                                  size="small"
                                  color={isPlaying ? 'primary' : 'default'}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    try {
                                      if (isPlaying) {
                                        if (audioRef.current) {
                                          audioRef.current.pause();
                                          audioRef.current.currentTime = 0;
                                          audioRef.current = null;
                                        }
                                        setPlayingKey(null);
                                        return;
                                      }
                                      if (audioRef.current) {
                                        audioRef.current.pause();
                                        audioRef.current.currentTime = 0;
                                        audioRef.current = null;
                                      }
                                      const audio = new Audio(url);
                                      audioRef.current = audio;
                                      setPlayingKey(key);
                                      audio.onended = () => {
                                        setPlayingKey(null);
                                        if (audioRef.current === audio) {
                                          audioRef.current = null;
                                        }
                                      };
                                      audio.onerror = () => {
                                        setPlayingKey(null);
                                        if (audioRef.current === audio) {
                                          audioRef.current = null;
                                        }
                                      };
                                      audio.play().catch(() => {
                                        setPlayingKey(null);
                                      });
                                    } catch {
                                      setPlayingKey(null);
                                    }
                                  }}
                                  aria-label={isPlaying ? `Stop sample ${i + 1}` : `Play sample ${i + 1}`}
                                >
                                  {isPlaying ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                                </IconButton>
                              );
                            })}
                          </Box>
                        </Stack>
                      </Paper>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">Karakter bilgisi yok</Typography>
                )}
              </Stack>
            </Paper>
          </Box>
        </Box>
      )}
      {!loading && !error && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 6 }}>
            <Button
              variant="contained"
              onClick={() => { setStartError(null); setStartOpen(true); }}
              sx={{
                bgcolor: 'warning.main',
                color: 'warning.contrastText',
                '&:hover': { bgcolor: 'warning.dark' },
              }}
            >
              Start New Step-2 Analysis
            </Button>
          </Box>
          <Box
            sx={{
              width: '75%',
              mx: 'auto',
              textAlign: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 1,
              py: 1.5,
              mb: 6,
              mt: 6,
            }}
          >
            <Typography variant="h6" sx={{ m: 0 }}>Step 2 - initial analysis of scenes and images</Typography>
          </Box>
          {runsLoading && (
            <Typography variant="body2" color="text.secondary">Yükleniyor...</Typography>
          )}
          {!runsLoading && runsError && (
            <Alert severity="error" sx={{ mb: 2 }}>{runsError}</Alert>
          )}
          {!runsLoading && !runsError && runs && runs.length === 0 && (
            <Alert severity="info">Henüz çalışma yok.</Alert>
          )}
          {!runsLoading && !runsError && runs && runs.length > 0 && (
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' } }}>
              {runs
                .slice()
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .map((r) => {
                  const defaultImg = 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRFBr8U-wvWLbkURPxHOF0zOmkrYNbOeSJXw_GEMckj8PHaJOthImF6WIbVyb_L_wfKUYQ&usqp=CAU';
                  const img = (r.baseImageUrl || '').trim() || defaultImg;
                  const isSelected = selectedRunId === r.id;
                  return (
                    <Card
                      key={r.id}
                      sx={{
                        position: 'relative',
                        border: 2,
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        boxShadow: isSelected ? 6 : 1,
                        transform: isSelected ? 'translateY(-2px)' : 'none',
                        transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                      }}
                    >
                      <CardActionArea aria-selected={isSelected} onClick={async () => {
                        setSelectedRunId(r.id);
                        setPlans(null);
                        setPlansError(null);
                        setPlansLoading(true);
                        try {
                          const data = await storyImagePlanService.getPlansByRunId(r.id);
                          setPlans(Array.isArray(data) ? data : []);
                        } catch (e: any) {
                          setPlansError(e?.response?.data?.message || e?.message || 'Image plans yüklenemedi');
                        } finally {
                          setPlansLoading(false);
                          // Smooth scroll to Step 3
                          setTimeout(() => {
                            if (step3Ref.current) {
                              step3Ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }, 0);
                        }
                      }}>
                        <CardMedia
                          component="img"
                          image={img}
                          alt={r.status}
                          sx={{ height: 160, objectFit: 'cover' }}
                          onError={(e) => {
                            const el = e.target as HTMLImageElement;
                            if (el && el.src !== defaultImg) {
                              el.src = defaultImg;
                            }
                          }}
                        />
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" noWrap>
                            #{r.runNumber ?? '-'}
                          </Typography>
                          <Typography variant="subtitle2" noWrap title={r.status}>
                            {r.status}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                            {r.mode} • {r.imageCount ?? 0} images
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {new Date(r.createdAt).toLocaleString()}
                          </Typography>
                          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate(`/runs/${r.id}`);
                              }}
                            >
                              Göster
                            </Button>
                          </Box>
                        </CardContent>
                      </CardActionArea>
                      {isSelected && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            boxShadow: 2,
                          }}
                          aria-label="Selected run"
                        >
                          <CheckCircleIcon fontSize="small" />
                          <Typography variant="caption">Selected</Typography>
                        </Box>
                      )}
                    </Card>
                  );
                })}
            </Box>
          )}
        </Box>
      )}
      {/* Step 3 - Image Plans */}
      {!loading && !error && (
        <Box ref={step3Ref} sx={{ mt: 4 }}>
          <Box
            sx={{
              width: '75%',
              mx: 'auto',
              textAlign: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 1,
              py: 1.5,
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ m: 0 }}>Step 3 - scene-by-scene image plans</Typography>
          </Box>
          {!selectedRunId && (
            <Alert severity="info">Bir çalışmayı görüntülemek için üstteki kartlardan birine tıklayın.</Alert>
          )}
          {selectedRunId && (
            <>
              {plansLoading && (
                <Typography variant="body2" color="text.secondary">Yükleniyor...</Typography>
              )}
              {!plansLoading && plansError && (
                <Alert severity="error" sx={{ mb: 2 }}>{plansError}</Alert>
              )}
              {!plansLoading && !plansError && plans && plans.length === 0 && (
                <Alert severity="info">Bu çalışma için plan bulunamadı.</Alert>
              )}
              {!plansLoading && !plansError && plans && plans.length > 0 && (
                <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' } }}>
                  {plans
                    .slice()
                    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))
                    .map((p, idx) => {
                      const defaultImg = 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRFBr8U-wvWLbkURPxHOF0zOmkrYNbOeSJXw_GEMckj8PHaJOthImF6WIbVyb_L_wfKUYQ&usqp=CAU';
                      const img = (p.imageUrl || '').trim() || defaultImg;
                      return (
                        <Card key={p.id} onClick={() => { setPlanIndex(idx); setPlanModalOpen(true); }} sx={{ cursor: 'pointer' }}>
                          <CardMedia
                            component="img"
                            image={img}
                            alt={p.description}
                            sx={{ height: 120, objectFit: 'cover' }}
                            onError={(e) => {
                              const el = e.target as HTMLImageElement;
                              if (el && el.src !== defaultImg) {
                                el.src = defaultImg;
                              }
                            }}
                          />
                          <CardContent sx={{ py: 1 }}>
                            <Typography variant="subtitle2" noWrap title={p.description}>
                              #{p.orderIndex} — {p.description}
                            </Typography>
                          </CardContent>
                        </Card>
                      );
                    })}
                </Box>
              )}
              {/* Modal for large view and full details */}
              <Dialog
                open={planModalOpen}
                onClose={() => setPlanModalOpen(false)}
                fullWidth
                maxWidth="md"
                onKeyDown={(e) => {
                  if (!plans) return;
                  if (e.key === 'ArrowLeft') setPlanIndex((i) => Math.max(0, i - 1));
                  if (e.key === 'ArrowRight') setPlanIndex((i) => Math.min(plans.length - 1, i + 1));
                }}
              >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  {/* <Typography variant="subtitle1" sx={{ mr: 2 }}>
                    {plans && plans[planIndex] ? `#${plans[planIndex].orderIndex} — ${plans[planIndex].description}` : 'Details'}
                  </Typography> */}
                  <IconButton onClick={() => setPlanModalOpen(false)} aria-label="Close">
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent
                  dividers
                  sx={{ maxHeight: { xs: '70vh', sm: '75vh' }, overflowY: 'auto' }}
                  onTouchStart={(e) => { touchStartX.current = e.changedTouches[0].clientX; }}
                  onTouchEnd={(e) => {
                    if (!plans) return;
                    const start = touchStartX.current;
                    const end = e.changedTouches[0].clientX;
                    if (start == null) return;
                    const delta = end - start;
                    if (Math.abs(delta) > SWIPE_THRESHOLD) {
                      if (delta < 0) setPlanIndex((i) => Math.min(plans.length - 1, i + 1));
                      else setPlanIndex((i) => Math.max(0, i - 1));
                    }
                    touchStartX.current = null;
                  }}
                >
                  {plans && plans[planIndex] && (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ position: 'relative' }}>
                          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <Box component="img"
                              src={(plans[planIndex].imageUrl || '').trim() || 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRFBr8U-wvWLbkURPxHOF0zOmkrYNbOeSJXw_GEMckj8PHaJOthImF6WIbVyb_L_wfKUYQ&usqp=CAU'}
                              alt={plans[planIndex].description}
                              style={{ width: '100%', height: 'auto', maxHeight: 480, objectFit: 'contain' }}
                              onError={(e: any) => {
                                const el = e.target as HTMLImageElement;
                                const fallback = 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRFBr8U-wvWLbkURPxHOF0zOmkrYNbOeSJXw_GEMckj8PHaJOthImF6WIbVyb_L_wfKUYQ&usqp=CAU';
                                if (el && el.src !== fallback) el.src = fallback;
                              }}
                            />
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => setFullImageOpen(true)}
                            sx={{ position: 'absolute', right: 8, bottom: 8, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                            aria-label="View full screen"
                          >
                            <SearchIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Stack spacing={1}>
                        {(() => {
                          const full = plans[planIndex].description || '';
                          const short = full.length > 50 ? full.slice(0, 50) + '…' : full;
                          const showToggle = full.length > 50;
                          return (
                            <Box>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                <strong>Description:</strong> {descExpanded ? full : short}
                              </Typography>
                              {showToggle && (
                                <Button size="small" onClick={() => setDescExpanded((v) => !v)} sx={{ mt: 0.5 }}>
                                  {descExpanded ? 'Read less' : 'Read more'}
                                </Button>
                              )}
                            </Box>
                          );
                        })()}
                        {(() => {
                          const full = plans[planIndex].textInImage || '';
                          const short = full.length > 50 ? full.slice(0, 50) + '…' : full;
                          const showToggle = full.length > 50;
                          return (
                            <Box>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                <strong>Text In Image:</strong> {textExpanded ? full : short}
                              </Typography>
                              {showToggle && (
                                <Button size="small" onClick={() => setTextExpanded((v) => !v)} sx={{ mt: 0.5 }}>
                                  {textExpanded ? 'Read less' : 'Read more'}
                                </Button>
                              )}
                            </Box>
                          );
                        })()}
                        {(() => {
                          const src = (plans[planIndex] as any)?.audioUrl as string | undefined;
                          if (!src) return null;
                          return (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <IconButton size="small" onClick={() => {
                                const el = modalAudioRef.current;
                                if (!el) return;
                                if (modalAudioPlaying) {
                                  try { el.pause(); } catch {}
                                  try { el.currentTime = 0; } catch {}
                                  setModalAudioPlaying(false);
                                } else {
                                  try { el.currentTime = 0; } catch {}
                                  el.play().then(() => setModalAudioPlaying(true)).catch(() => {});
                                }
                              }} aria-label="Play audio">
                                {modalAudioPlaying ? <StopIcon /> : <PlayArrowIcon />}
                              </IconButton>
                              <Typography variant="body2" color="text.secondary">
                                {modalAudioDuration != null ? `Audio: ${Math.round(modalAudioDuration)} sn` : 'Audio'}
                              </Typography>
                              <audio
                                ref={(el) => {
                                  modalAudioRef.current = el;
                                  if (el) {
                                    el.onended = () => setModalAudioPlaying(false);
                                    el.onloadedmetadata = () => setModalAudioDuration(el.duration);
                                  }
                                }}
                                src={src}
                                preload="metadata"
                              />
                            </Stack>
                          );
                        })()}
                        <Typography variant="body2"><strong>Order:</strong> {plans[planIndex].orderIndex}</Typography>
                        <Typography variant="body2"><strong>Duration:</strong> {plans[planIndex].durationMs} ms - {Math.round(plans[planIndex].durationMs / 1000)} sn</Typography>
                        <Typography variant="body2"><strong>Start:</strong> {plans[planIndex].startMs} ms - {Math.round(plans[planIndex].startMs / 1000)} sn</Typography>
                        <Typography variant="body2"><strong>Status:</strong> {plans[planIndex].status}</Typography>
                        <Typography variant="body2"><strong>Created At:</strong> {new Date(plans[planIndex].createdAt).toLocaleString()}</Typography>
                      </Stack>
                    </>
                  )}
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between' }}>
                  <IconButton
                    onClick={() => plans && setPlanIndex((i) => Math.max(0, i - 1))}
                    disabled={!plans || planIndex <= 0}
                    aria-label="Previous"
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => plans && setPlanIndex((i) => Math.min((plans?.length || 1) - 1, i + 1))}
                    disabled={!plans || planIndex >= (plans?.length || 1) - 1}
                    aria-label="Next"
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </DialogActions>
              </Dialog>
              {/* Fullscreen image dialog */}
              <Dialog
                open={fullImageOpen}
                onClose={() => setFullImageOpen(false)}
                fullScreen
              >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle1" sx={{ mr: 2 }}>
                    {plans && plans[planIndex] ? `#${plans[planIndex].orderIndex} — ${plans[planIndex].description}` : 'Image'}
                  </Typography>
                  <IconButton onClick={() => setFullImageOpen(false)} aria-label="Close">
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, bgcolor: 'black', overflow: 'auto' }}>
                  {plans && plans[planIndex] && (
                    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box
                        component="img"
                        src={(plans[planIndex].imageUrl || '').trim() || 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRFBr8U-wvWLbkURPxHOF0zOmkrYNbOeSJXw_GEMckj8PHaJOthImF6WIbVyb_L_wfKUYQ&usqp=CAU'}
                        alt={plans[planIndex].description}
                        sx={{ width: 'auto', height: 'auto', maxWidth: 'none', maxHeight: 'none', display: 'block' }}
                        onError={(e: any) => {
                          const el = e.target as HTMLImageElement;
                          const fallback = 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRFBr8U-wvWLbkURPxHOF0zOmkrYNbOeSJXw_GEMckj8PHaJOthImF6WIbVyb_L_wfKUYQ&usqp=CAU';
                          if (el && el.src !== fallback) el.src = fallback;
                        }}
                      />
                    </Box>
                  )}
                </DialogContent>
              </Dialog>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TemplateDetailPage;
